"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "utils";
import { useSnapshot } from "valtio";
import type {
  ChatRefContent,
  SenderProps,
  SenderSubmitOptions,
} from "../Sender/type";
import type { Question } from "../Sender/QuestionItem";
import { HomeStore } from "@/src/store";
import { I18nUtil } from "@utils/i18n";

import { Sender } from "@components";
import Translations from "@translate/components/Home/HomePageSender";
import { useContainerPosition } from "@/src/hooks/useContainerPosition";
import { useIsPc } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

interface HomePageSenderProps extends SenderProps {
  data?: any;
  onSend: (v: string | undefined, options?: SenderSubmitOptions) => unknown;
  loading?: boolean;
  value: string;
  onChange?: (e: any) => any;
  onQuestionSelect?: (question: Question) => void;
  chatRefContent?: ChatRefContent | null;
  onCloseChatToast?: () => void;
  className?: string;
}

export const HomePageSender = ({
  data = [],
  className,
  onSend,
  loading,
  value,
  onChange,
  onQuestionSelect,
  chatRefContent,
  onCloseChatToast,
  ...rest
}: HomePageSenderProps) => {
  // 跟踪当前显示的起始索引，用于循环显示推荐问题
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [isPC] = useIsPc();

  // 根据 currentStartIndex 获取当前要显示的 4 条问题
  const questions = useMemo<Question[]>(() => {
    if (data.length === 0) {
      return [];
    }
    // 从 currentStartIndex 开始取 4 条
    return data.slice(currentStartIndex, currentStartIndex + 4);
  }, [data, currentStartIndex]);

  // 获取下一组问题的起始索引
  const getNextStartIndex = useCallback(() => {
    const nextIndex = currentStartIndex + 4;
    // 如果超出范围，回到开头
    return nextIndex >= data.length ? 0 : nextIndex;
  }, [currentStartIndex, data.length]);

  // 当数据变化时，确保 currentStartIndex 不超出范围
  useEffect(() => {
    if (data.length > 0 && currentStartIndex >= data.length) {
      setCurrentStartIndex(0);
    }
  }, [data.length, currentStartIndex]);

  const { isOpen } = useSnapshot(HomeStore);

  // 获取 MarketSummary 容器的位置和宽度信息
  const containerPosition = useContainerPosition({
    selector: '[data-container="main-content"]',
    refreshTrigger: isOpen,
  });

  // 动态计算容器样式
  const containerStyle: React.CSSProperties = useMemo(() => {
    if (!isPC) {
      return {};
    }
    if (containerPosition.isReady && containerPosition.width > 0) {
      return {
        width: `${containerPosition.width}px`,
        left: `${containerPosition.left}px`,
        right: "auto",
      };
    }
    // 如果未获取到容器信息，使用默认样式（居中）
    return {
      width: "100%",
    };
  }, [containerPosition]);

  // 处理输入框聚焦事件，刷新推荐问题并切换到下一组
  const handleFocus = useCallback(() => {
    // 切换到下一组问题（循环显示）
    setCurrentStartIndex(getNextStartIndex());
  }, [getNextStartIndex]);

  // 如果容器信息未就绪，隐藏组件
  if ((!containerPosition.isReady || containerPosition.width <= 0) && isPC) {
    return null;
  }

  return (
    <div
      className={cn(
        "md:fixed md:bottom-0 md:z-10",
        "max-md:px-4",
        "py-4",
        className,
      )}
      style={containerStyle}
      data-tracking-scope="sender"
    >
      <div className="relative w-full mx-auto flex-1 flex rounded-4 md:no-scrollbar mt-6 h-fit relative min-w-0 bg-background_solid_secondary/80 backdrop-blur-md">
        <Sender
          containerClassName="w-full"
          onSend={onSend}
          loading={loading}
          value={value}
          onChange={onChange}
          questions={questions}
          onQuestionSelect={onQuestionSelect}
          placeholder={I18n.t("Ask a question to get started")}
          chatRefContent={chatRefContent || undefined}
          onCloseChatToast={onCloseChatToast}
          onFocus={handleFocus}
          {...rest}
        />
      </div>
    </div>
  );
};
